import { Link } from "react-router-dom";
import { ArrowLeft, Languages } from "lucide-react";
import { Reveal, PageHero } from "@/components/Motion";
import { CONTACTS } from "@/data/content";
import { useLanguage } from "@/i18n";

const Section = ({ num, title, children }) => (
  <Reveal>
    <section data-testid={`cookie-section-${num}`} className="border-t border-stone-300 pt-10">
      <div className="flex items-baseline gap-5">
        <span className="font-mono text-xs tracking-[0.25em] text-[#8F6B3C]">{num}</span>
        <h2 className="font-serif text-2xl uppercase tracking-wide text-stone-900 md:text-3xl">
          {title}
        </h2>
      </div>
      <div className="mt-5 space-y-4 text-sm leading-relaxed text-stone-500 md:text-base">
        {children}
      </div>
    </section>
  </Reveal>
);

export default function CookiePolicy() {
  const { t, lang } = useLanguage();

  return (
    <main data-testid="cookie-policy-page">
      <PageHero
        eyebrow={t("privacyEyebrow")}
        lines={[t("cookieL1"), t("cookieL2")]}
        sub={t("cookieSub")}
      />

      <div className="mx-auto max-w-4xl space-y-14 px-6 pb-24 md:pb-32">
        {lang === "en" && (
          <Reveal>
            <div data-testid="cookie-en-note" className="border border-[#C89D66]/40 bg-[#C89D66]/5 p-6 md:p-8">
              <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-[#8F6B3C]">
                <Languages className="h-4 w-4" />
                {t("legalEnNoteTitle")}
              </p>
              <p className="mt-3 text-sm leading-relaxed text-stone-600">{t("legalEnNoteText")}</p>
            </div>
          </Reveal>
        )}

        <Section num="01" title="Cosa sono i cookie">
          <p>
            I cookie sono piccoli file di testo che i siti visitati inviano al dispositivo
            dell'utente, dove vengono memorizzati per essere ritrasmessi agli stessi siti alla
            visita successiva. Tecnologie analoghe, come il local storage del browser, hanno
            finalità equiparabili.
          </p>
        </Section>

        <Section num="02" title="Cookie tecnici di questo sito">
          <p>
            Questo sito utilizza esclusivamente <strong className="text-stone-800">cookie tecnici e
            strumenti equivalenti</strong>, necessari al funzionamento delle pagine. Non richiedono
            il consenso preventivo dell'utente:
          </p>
          <ul className="list-disc space-y-2 pl-5">
            <li>
              <strong className="text-stone-800">Preferenza consenso cookie</strong> — memorizza
              l'accettazione del banner informativo (local storage, chiave
              "edilcomplex-cookie-consent");
            </li>
            <li>
              <strong className="text-stone-800">Preferenza lingua</strong> — memorizza la lingua
              di visualizzazione scelta, italiano o inglese (local storage, chiave "ec-lang");
            </li>
            <li>
              Cookie tecnici di sessione eventualmente rilasciati dall'infrastruttura di hosting
              per garantire sicurezza e bilanciamento del carico.
            </li>
          </ul>
          <p>
            Non vengono utilizzati cookie di profilazione, remarketing o pubblicitari. I cookie
            analitici di Google Analytics 4 vengono installati solo previo consenso espresso
            tramite il banner cookie e possono essere rifiutati o revocati in qualsiasi momento.
          </p>
        </Section>

        <Section num="03" title="Contenuti e servizi di terze parti">
          <p>
            Il sito incorpora contenuti di servizi terzi che potrebbero rilasciare propri cookie
            secondo le rispettive informative:
          </p>
          <ul className="list-disc space-y-2 pl-5">
            <li>
              <strong className="text-stone-800">Google Analytics 4</strong> (Google Ireland Ltd.) —
              statistiche aggregate di navigazione con IP anonimizzato, cookie "_ga" e "_ga_*"
              (durata massima 14 mesi), attivati solo previo consenso tramite il banner.{" "}
              <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-[#8F6B3C] hover:underline">
                Informativa Google
              </a>;
            </li>
            <li>
              <strong className="text-stone-800">Google Maps</strong> (Google Ireland Ltd.) — mappa
              interattiva nella pagina Contatti.{" "}
              <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-[#8F6B3C] hover:underline">
                Informativa Google
              </a>;
            </li>
            <li>
              <strong className="text-stone-800">Google Fonts</strong> (Google Ireland Ltd.) —
              caricamento dei caratteri tipografici;
            </li>
            <li>
              <strong className="text-stone-800">WhatsApp</strong> (Meta Platforms, Inc.) — i
              pulsanti di contatto aprono l'applicazione o il sito di WhatsApp tramite link
              esterno: nessun cookie viene installato da questo sito.
            </li>
          </ul>
        </Section>

        <Section num="04" title="Gestione dei cookie dal browser">
          <p>
            L'utente può bloccare o eliminare i cookie in qualsiasi momento tramite le impostazioni
            del proprio browser:
          </p>
          <ul className="list-disc space-y-2 pl-5">
            <li>Chrome: Impostazioni → Privacy e sicurezza → Cookie</li>
            <li>Firefox: Impostazioni → Privacy e sicurezza → Cookie e dati dei siti web</li>
            <li>Safari: Preferenze → Privacy → Cookie e dati dei siti web</li>
            <li>Edge: Impostazioni → Cookie e autorizzazioni sito</li>
          </ul>
          <p>
            La disattivazione dei cookie tecnici può compromettere alcune funzionalità del sito,
            come la memorizzazione della lingua scelta.
          </p>
        </Section>

        <Section num="05" title="Modifiche e contatti">
          <p>
            La presente Cookie Policy può essere aggiornata nel tempo: si invita a consultarla
            periodicamente. Per qualsiasi domanda è possibile scrivere a{" "}
            <a href={`mailto:${CONTACTS.email}`} className="text-[#8F6B3C] hover:underline">{CONTACTS.email}</a>.
          </p>
          <p>
            Titolare del trattamento: {CONTACTS.legalName} · P.IVA {CONTACTS.piva} ·{" "}
            {CONTACTS.address}.
          </p>
        </Section>

        <Reveal>
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-stone-500">
            {t("lastUpdate")}
          </p>
        </Reveal>

        <Reveal>
          <Link
            data-testid="cookie-back-home"
            to="/"
            className="group inline-flex items-center gap-3 border border-stone-300 px-6 py-3.5 font-mono text-[11px] uppercase tracking-[0.2em] text-stone-900 transition-colors duration-300 hover:border-[#C89D66] hover:text-[#8F6B3C]"
          >
            <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" />
            {t("backHome")}
          </Link>
        </Reveal>
      </div>
    </main>
  );
}
